import axios from "axios"
import { createClient } from "./supabase/component"
import { getActiveProjectIdForAxios } from "@/contexts/ProjectContext"

/**
 * Browser: always same origin as the page so `/v1.0/...` stays same-site (no CORS).
 * Do not use NEXT_PUBLIC_BASE_URL / API host here — that breaks the dashboard when
 * the marketing URL is seedmancer.dev and NEXT_PUBLIC_BASE_URL points at api.*.
 *
 * Server (SSR): absolute site URL from env or Vercel.
 */
function resolveBaseURL(): string {
  if (typeof window !== "undefined") {
    return window.location.origin
  }
  const site = process.env.NEXT_PUBLIC_BASE_URL?.replace(/\/$/, "")
  if (site) return site
  const vercel = process.env.VERCEL_URL
  if (vercel) {
    return vercel.startsWith("http") ? vercel : `https://${vercel}`
  }
  return ""
}

const axiosInstance = axios.create({
  baseURL: resolveBaseURL(),
  withCredentials: true
})

axiosInstance.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    config.baseURL = window.location.origin
  }
  return config
})

axiosInstance.interceptors.request.use(
  async (config) => {
    const { data } = await createClient().auth.getSession()
    const token = data.session?.access_token
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Inject active project ID so all API requests are scoped to the active project
axiosInstance.interceptors.request.use((config) => {
  const projectId = getActiveProjectIdForAxios()
  if (projectId && config.headers) {
    config.headers["X-Project-Id"] = projectId
  }
  return config
})

export default axiosInstance
